import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Button,
  Alert,
} from '@mui/material';

const Documentation = () => {
  console.log('Documentation component is rendering!');
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #10b981 100%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            📚 Documentation
          </Typography>
          <Typography
            variant="h5"
            sx={{ color: 'text.secondary', mb: 4, maxWidth: 800, mx: 'auto', lineHeight: 1.6 }}
          >
            Learn how to embed Discord chat directly on your website with just a few lines of code
          </Typography>
        </Box>

        {/* Quick Start Guide */}
        <Card sx={{ 
          mb: 6,
          background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
          backdropFilter: 'blur(20px)',
        }}>
          <CardContent sx={{ p: 6 }}>
            <Typography variant="h3" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
              🚀 Quick Start Guide
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', fontSize: '1.1rem' }}>
              Get Discord chat on your website in under 5 minutes. No tricky setup required!
            </Typography>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Step 1: Register Your Account
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                Create a Wizcord account and associate your Discord server with it.
              </Typography>
              
              <Paper sx={{ 
                p: 4, 
                backgroundColor: 'rgba(16, 185, 129, 0.15)',
                border: '2px solid',
                borderColor: 'secondary.main',
                borderRadius: 3,
                background: 'linear-gradient(145deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                mb: 4
              }}>
                <Typography variant="h6" fontWeight="bold" color="secondary.main" sx={{ mb: 3 }}>
                  🔐 Account Setup
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontSize: '14px', lineHeight: 1.6 }}>
                  1. Register for a free Wizcord account<br/>
                  2. Get your Discord Server ID (see instructions below)<br/>
                  3. Enter the Server ID in your account to associate it<br/>
                </Typography>
                
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, fontSize: '14px' }}>
                  How to get your Discord Server ID:
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontSize: '13px', lineHeight: 1.5, color: 'text.secondary' }}>
                  • Enable Developer Mode: User Settings → Advanced → Developer Mode<br/>
                  • Right-click on your Discord server name (in the server list)<br/>
                  • Select "Copy Server ID" from the context menu<br/>
                  • Paste this ID in your Wizcord account settings
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  sx={{ 
                    bgcolor: 'secondary.main',
                    color: 'rgba(15, 23, 42, 0.9)',
                    fontWeight: 700,
                    fontSize: '1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
                    '&:hover': { 
                      bgcolor: 'secondary.light',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4)',
                    }
                  }}
                  href="/register"
                >
                  🚀 Register Account
                </Button>
              </Paper>
            </Box>

            <Box sx={{ mb: 6 }} id="step-2">
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Step 2: Install the Bot
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                After registering your account, install our bot to your Discord server.
              </Typography>
              
              <Paper sx={{ 
                p: 4, 
                backgroundColor: 'rgba(99, 102, 241, 0.15)',
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 3,
                background: 'linear-gradient(145deg, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)',
                textAlign: 'center',
                mb: 4
              }}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ mb: 3 }}>
                  🤖 Install Wizcord Bot
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontSize: '14px', lineHeight: 1.6 }}>
                  Click the button below to add the Wizcord bot to your Discord server.<br/>
                  You'll need "Manage Server" permissions to install the bot.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  sx={{ 
                    bgcolor: '#5865F2',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '1rem',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(88, 101, 242, 0.3)',
                    '&:hover': { 
                      bgcolor: '#4752C4',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(88, 101, 242, 0.4)',
                    }
                  }}
                  href="https://discord.com/oauth2/authorize?client_id=1394670689075597393&permissions=536939520&integration_type=0&scope=bot"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📥 Add Bot to Server
                </Button>
              </Paper>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Step 3: Get Your Channel ID & Configuration
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, fontSize: '14px' }}>
                How to get your Discord Channel ID:
              </Typography>
              <Typography variant="body2" sx={{ mb: 4, fontSize: '13px', lineHeight: 1.5, color: 'text.secondary' }}>
                • Enable Developer Mode: User Settings → Advanced → Developer Mode<br/>
                • Right-click on your Discord channel (in the channel list)<br/>
                • Select "Copy Channel ID" from the context menu<br/>
                • Save this ID for use in the iframe
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Configuration Parameters:
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ 
                    p: 4, 
                    backgroundColor: 'background.paper',
                    border: '3px solid',
                    borderColor: 'primary.main',
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                  }}>
                    <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
                      channelId
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      backgroundColor: 'primary.main', 
                      color: 'white', 
                      px: 2, 
                      py: 1, 
                      borderRadius: 2, 
                      fontSize: '12px',
                      fontWeight: 'bold',
                      display: 'inline-block',
                      mb: 2
                    }}>
                      REQUIRED
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      Discord channel ID where messages will be displayed. Copy this from Discord using the steps above.
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper sx={{ 
                    p: 4, 
                    backgroundColor: 'background.paper',
                    border: '3px solid',
                    borderColor: 'secondary.main',
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                  }}>
                    <Typography variant="h6" fontWeight="bold" color="secondary.main" sx={{ mb: 2 }}>
                      📱 Menu Parameter (Optional)
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      backgroundColor: 'secondary.main', 
                      color: 'black', 
                      px: 2, 
                      py: 1, 
                      borderRadius: 2, 
                      fontSize: '12px',
                      fontWeight: 'bold',
                      display: 'inline-block',
                      mb: 2
                    }}>
                      OPTIONAL
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                      Controls whether the channel sidebar is shown:<br/>
                      • <code>menu=true</code> - Shows sidebar (default)<br/>
                      • <code>menu=false</code> - Hides sidebar<br/>
                      <strong>Note: When enabled, users can switch between channels</strong>
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ mb: 6 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Step 4: Add the iframe to your HTML
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 3, fontWeight: 600 }}>
                Basic Setup:
              </Typography>
              <Paper sx={{ 
                p: 4, 
                backgroundColor: '#0a0a0a', 
                borderRadius: 3,
                border: '2px solid rgba(99, 102, 241, 0.3)',
                mb: 4
              }}>
                <pre style={{ 
                  margin: 0, 
                  color: '#f8f8f2', 
                  fontSize: '14px', 
                  lineHeight: 1.6,
                  overflow: 'auto'
                }}>
                  <code>
                    <span style={{ color: '#f8f8f2' }}>&lt;iframe</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  src="</span>
                    <span style={{ color: '#6366f1' }}>https://wizcord.io/iframe?channelId=</span>
                    <span style={{ color: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.2)', padding: '2px 6px', borderRadius: '4px' }}>YOUR_CHANNEL_ID</span>
                    <span style={{ color: '#f8f8f2' }}>"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  width="800px"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  height="600px"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  frameBorder="0"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  title="Discord Chat"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  style="border-radius: 10px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>&gt;&lt;/iframe&gt;</span>
                  </code>
                </pre>
              </Paper>

              <Typography variant="body1" sx={{ mb: 3, fontWeight: 600 }}>
                Without Sidebar:
              </Typography>
              <Paper sx={{ 
                p: 4, 
                backgroundColor: '#0a0a0a', 
                borderRadius: 3,
                border: '2px solid rgba(99, 102, 241, 0.3)'
              }}>
                <pre style={{ 
                  margin: 0, 
                  color: '#f8f8f2', 
                  fontSize: '14px', 
                  lineHeight: 1.6,
                  overflow: 'auto'
                }}>
                  <code>
                    <span style={{ color: '#f8f8f2' }}>&lt;iframe</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  src="</span>
                    <span style={{ color: '#6366f1' }}>https://wizcord.io/iframe?channelId=</span>
                    <span style={{ color: '#10b981', backgroundColor: 'rgba(16, 185, 129, 0.2)', padding: '2px 6px', borderRadius: '4px' }}>YOUR_CHANNEL_ID</span>
                    <span style={{ color: '#6366f1' }}>&menu=</span>
                    <span style={{ color: '#8b5cf6', backgroundColor: 'rgba(139, 92, 246, 0.2)', padding: '2px 6px', borderRadius: '4px' }}>false</span>
                    <span style={{ color: '#f8f8f2' }}>"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  width="800px"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  height="600px"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  frameBorder="0"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  title="Discord Chat"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>  style="border-radius: 10px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);"</span><br/>
                    <span style={{ color: '#f8f8f2' }}>&gt;&lt;/iframe&gt;</span>
                  </code>
                </pre>
              </Paper>
              
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.5 }}>
                Replace <code style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', padding: '2px 4px', borderRadius: '4px', color: '#10b981' }}>YOUR_CHANNEL_ID</code> with the ID you copied from Discord. 
                Use <code style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', padding: '2px 4px', borderRadius: '4px', color: '#8b5cf6' }}>menu=false</code> to hide the channel sidebar.
              </Typography>
              <Alert severity="info" sx={{ 
                mt: 3,
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: 2,
              }}>
                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                  <strong>💡 Pro Tip:</strong> The sidebar is enabled by default. Add <code style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', padding: '2px 4px', borderRadius: '4px', color: '#8b5cf6' }}>menu=false</code> for a cleaner, single-channel view without channel switching.
                </Typography>
              </Alert>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Step 5: That's it! 🎉
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
                Your Discord chat is now embedded on your website. Users can:
              </Typography>
              <Box component="ul" sx={{ marginLeft: '24px', lineHeight: 2 }}>
                <li>Chat as guests with custom usernames</li>
                <li>Login with their Discord accounts</li>
                <li>See real-time messages</li>
                <li>View message history</li>
                <li>Switch between channels (if menu is enabled)</li>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Features Grid */}
        <Typography variant="h3" sx={{ 
          textAlign: 'center', 
          mb: 6, 
          fontWeight: 700,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}>
          ✨ Features
        </Typography>
        
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {[
            {
              icon: '🔐',
              title: 'Discord Authentication',
              description: 'Users can login with their Discord accounts or chat as guests'
            },
            {
              icon: '⚡',
              title: 'Real-time Updates',
              description: 'Messages appear instantly with WebSocket connections'
            },
            {
              icon: '📱',
              title: 'Responsive Design',
              description: 'Works perfectly on desktop, tablet, and mobile devices'
            },
            {
              icon: '🎛️',
              title: 'Channel Switching',
              description: 'Optional sidebar (menu=true) shows all accessible channels for switching'
            },
            {
              icon: '🎨',
              title: 'Discord UI',
              description: 'Familiar Discord interface with dark theme and styling'
            },
            {
              icon: '🛡️',
              title: 'Secure',
              description: 'Built-in security measures and permission validation'
            }
          ].map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ 
                height: '100%', 
                textAlign: 'center', 
                p: 4,
                background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
                backdropFilter: 'blur(10px)',
              }}>
                <Typography variant="h2" sx={{ 
                  mb: 3, 
                  fontSize: '3rem',
                  filter: 'drop-shadow(0 0 10px rgba(99, 102, 241, 0.3))',
                }}>
                  {feature.icon}
                </Typography>
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* CTA Section */}
        <Paper sx={{ 
          p: 6, 
          textAlign: 'center', 
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          borderRadius: 4,
          border: 'none',
        }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
            Ready to Add Discord Chat to Your Site?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.95, lineHeight: 1.6 }}>
            Join thousands of developers who are already using Wizcord to enhance their websites
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{ 
                bgcolor: 'secondary.main',
                color: 'rgba(15, 23, 42, 0.9)',
                fontWeight: 700,
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                border: '2px solid rgba(16, 185, 129, 0.3)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                '&:hover': { 
                  bgcolor: 'secondary.light',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 15px 35px rgba(0, 0, 0, 0.4)',
                  borderColor: 'rgba(16, 185, 129, 0.6)',
                }
              }}
              href="/register"
            >
              🚀 Get Started Free
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Documentation; 